import { Flex, Heading, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Option, Select, TextField } from '@main/complykit';
import { Card } from '@main/complykit/components/card';
import { Content, Footer, Header } from '@main/complykit/components/slots';
import { Integration_Auth_Names_Enum, Integration_Names_Enum } from '@main/graphql/types.generated';
import { IntegrationConnector } from '@main/integrations';
import { getRegisteredIntegrationConnector } from '@main/integrations/registry/connectors';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod/v4';

export type AddConnectorCardProps = {
  onAdd: (payload: AddConnectorPayload) => Promise<void>;
};

type AddConnectorPayload = {
  connectorName: string;
  providerName: Integration_Names_Enum;
  authName: Integration_Auth_Names_Enum;
};

const formSchema = z.object({
  connectorName: IntegrationConnector.nameSchema,
  providerName: z.enum(Integration_Names_Enum, {
    error: 'addConnectorCard.form.providerName.validation',
  }),
  authName: z.enum(Integration_Auth_Names_Enum, {
    error: 'addConnectorCard.form.authName.validation',
  }),
});

export const AddConnectorCard = ({ onAdd }: AddConnectorCardProps) => {
  const { t } = useTranslation('integrations');
  const { t: tShared } = useTranslation();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      connectorName: '',
    },
  });

  const providerNameOptions = useProviderNameOptions();
  const currentProviderName = form.watch('providerName');

  const authNameOptions = useAuthNameOptions(currentProviderName);

  useEffect(() => {
    const [firstOption] = authNameOptions;
    if (firstOption) {
      form.setValue('authName', firstOption.value);
    }
  }, [form, authNameOptions]);

  return (
    <Card>
      <Header>
        <Heading size="sm">{t('addConnectorCard.title')}</Heading>
      </Header>

      <Content>
        <Stack spacing={4}>
          <Controller
            control={form.control}
            name="connectorName"
            render={({ field, fieldState }) => (
              <TextField
                ref={field.ref}
                label={t('addConnectorCard.form.connectorName.label')}
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={t('addConnectorCard.form.connectorName.placeholder')}
                error={
                  fieldState.error && t('addConnectorCard.form.connectorName.validation.required')
                }
              />
            )}
          />

          <Controller
            control={form.control}
            name="providerName"
            render={({ field, fieldState }) => (
              <Select
                ref={field.ref}
                label={t('addConnectorCard.form.providerName.label')}
                isClearable
                clearLabel={t('clearSelection', {
                  name: t('addConnectorCard.form.providerName.label'),
                })}
                name={field.name}
                value={field.value ?? null}
                onChange={(id) => {
                  const option = providerNameOptions.find((provider) => provider.value === id);
                  field.onChange(option?.value);
                }}
                onBlur={field.onBlur}
                isDisabled={field.disabled}
                error={fieldState.error?.message && t(fieldState.error.message as never)}
                placeholder={t('addConnectorCard.form.providerName.placeholder')}
                isFilterable
                filterLabel={tShared('pickers.filter')}
                filterPlaceholder={tShared('pickers.filterPlaceholder')}
                filterEmptyMessage={tShared('pickers.noMatches')}
                emptyMessage={tShared('pickers.noOptions')}
                items={providerNameOptions}
              >
                {(option) => <Option id={option.value}>{option.label}</Option>}
              </Select>
            )}
          />

          <Controller
            control={form.control}
            name="authName"
            render={({ field, fieldState }) => (
              <Select
                ref={field.ref}
                label={t('addConnectorCard.form.authName.label')}
                isClearable
                clearLabel={t('clearSelection', {
                  name: t('addConnectorCard.form.authName.label'),
                })}
                name={field.name}
                value={field.value ?? null}
                onChange={(id) => {
                  const option = authNameOptions.find((auth) => auth.value === id);
                  field.onChange(option?.value);
                }}
                onBlur={field.onBlur}
                isDisabled={field.disabled || authNameOptions.length < 2}
                error={fieldState.error?.message && t(fieldState.error.message as never)}
                placeholder={t('addConnectorCard.form.authName.placeholder')}
                items={authNameOptions}
                emptyMessage={tShared('pickers.noOptions')}
              >
                {(option) => <Option id={option.value}>{option.label}</Option>}
              </Select>
            )}
          />
        </Stack>
      </Content>

      <Footer>
        <Flex justify="end" w="full">
          <Button
            isPending={form.formState.isSubmitting}
            onPress={() =>
              void form.handleSubmit(async (data) => {
                await onAdd(data);
                form.reset();
              })()
            }
          >
            {t('addConnectorCard.form.submitBtn')}
          </Button>
        </Flex>
      </Footer>
    </Card>
  );
};

function useProviderNameOptions() {
  const { t } = useTranslation('integrations');

  return useMemo(() => {
    return Object.values(Integration_Names_Enum)
      .sort()
      .map((value) => ({
        value,
        label: t(`integrations.${value}.name`),
      }));
  }, [t]);
}

function useAuthNameOptions(providerName?: Integration_Names_Enum) {
  const { t } = useTranslation('integrations');

  return useMemo(() => {
    if (!providerName) {
      return [];
    }
    const connector = getRegisteredIntegrationConnector(providerName);
    return connector.auths.group.map(({ name, deprecated }) => ({
      value: name,
      label: [t(`auths.${name}.name`), deprecated ? `[${t('deprecatedLbl')}]` : null]
        .filter(Boolean)
        .join(' '),
    }));
  }, [providerName, t]);
}
